import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useAuth from "../../../../auth/useAuth";
import { Alerts } from "../../../../components/alerts/Alerts.tsx";
import { removeOrderFromRoute, optimizeRoute, approveRoute, rejectRoute, moveOrderToRoute } from "../../../../config";
import ConfirmDialog from "../../../dialogs/confirm/ConfirmDialog";
import ApproveRouteDialog from "./ApproveRouteDialog";
import MoveOrderDialog from "./MoveOrderDialog";

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
};

const RouteCard = ({ route, onActionComplete }) => {
  const auth = useAuth();
  const token = auth.globals.token;
  const { alertSuccess, showErrorRequest } = Alerts();

  const [expanded, setExpanded] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [removingBoxId, setRemovingBoxId] = useState(null);
  const [confirmRemoveBoxId, setConfirmRemoveBoxId] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [moveBoxId, setMoveBoxId] = useState(null);

  const handleApiError = async (resp, fallbackMessage) => {
    if (resp.status === 401 || resp.status === 403) {
      showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
      return;
    }
    const errorData = await resp.json().catch(() => null);
    showErrorRequest(errorData?.detail || errorData?.message || fallbackMessage, auth);
  };

  const handleOptimize = async () => {
    try {
      setOptimizing(true);
      const resp = await optimizeRoute(token, route.id);
      if (resp.ok) {
        alertSuccess("Éxito", "Ruta optimizada correctamente");
        onActionComplete();
      } else {
        await handleApiError(resp, "No fue posible optimizar la ruta");
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setOptimizing(false);
    }
  };

  const handleRemoveOrder = async (box_id) => {
    try {
      setRemovingBoxId(box_id);
      const resp = await removeOrderFromRoute(token, route.id, box_id);
      if (resp.ok) {
        alertSuccess("Éxito", "Orden quitada de la ruta");
        onActionComplete();
      } else {
        await handleApiError(resp, "No fue posible quitar la orden");
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setRemovingBoxId(null);
      setConfirmRemoveBoxId(null);
    }
  };

  const handleReject = async () => {
    try {
      setRejecting(true);
      const resp = await rejectRoute(token, route.id);
      if (resp.ok) {
        alertSuccess("Éxito", "Ruta rechazada");
        onActionComplete();
      } else {
        await handleApiError(resp, "No fue posible rechazar la ruta");
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setRejecting(false);
      setConfirmReject(false);
    }
  };

  const handleApprove = async (driverId) => {
    try {
      const resp = await approveRoute(token, route.id, driverId);
      if (resp.ok) {
        alertSuccess("Éxito", "Ruta aprobada y asignada");
        setApproveDialogOpen(false);
        onActionComplete();
      } else {
        await handleApiError(resp, "No fue posible aprobar la ruta");
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    }
  };

  const handleMoveOrder = async (targetRouteId) => {
    try {
      const resp = await moveOrderToRoute(token, route.id, moveBoxId, targetRouteId);
      if (resp.ok) {
        alertSuccess("Éxito", "Orden movida a la otra ruta");
        setMoveBoxId(null);
        onActionComplete();
      } else {
        await handleApiError(resp, "No fue posible mover la orden");
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography fontWeight="bold">
              Ruta #{route.id} — {route.ring_name || route.base_origin}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Creada: {route.creation_date}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {[
              `Paradas: ${route.total_stops ?? route.total_orders}`,
              `Órdenes: ${route.total_orders}`,
              route.distance_meters != null && `Distancia: ${(route.distance_meters / 1000).toFixed(1)} km`,
              formatDuration(route.total_duration_seconds) && `Tiempo est.: ${formatDuration(route.total_duration_seconds)}`,
            ].filter(Boolean).join(" | ")}
          </Typography>
          {route.ring_name && (
            <Typography variant="body2" color="text.secondary">
              Base: {route.base_origin}
            </Typography>
          )}
          {route.status === "aprobada" && route.driver_name && (
            <Typography variant="body2" color="text.secondary">
              Piloto: {route.driver_name} {route.driver_lastname}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {(route.orders || []).map((order, idx) => {
            const sameStop =
              idx > 0 &&
              order.visit_order != null &&
              order.visit_order === route.orders[idx - 1]?.visit_order;
            return (
              <ListItem
                key={`${order.box_id}_${idx}`}
                divider
                secondaryAction={
                  <Box display="flex" gap={1}>
                    <Button size="small" variant="outlined" onClick={() => setMoveBoxId(order.box_id)}>
                      Mover
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={removingBoxId === order.box_id}
                      onClick={() => setConfirmRemoveBoxId(order.box_id)}
                    >
                      {removingBoxId === order.box_id ? <CircularProgress size={16} /> : "Quitar"}
                    </Button>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <>
                      #{idx + 1} · {order.correlative} — {order.senders_name}
                      {order.visit_order != null && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Parada {order.visit_order}
                        </Typography>
                      )}
                      {sameStop && (
                        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1, fontStyle: "italic" }}>
                          · misma parada
                        </Typography>
                      )}
                    </>
                  }
                  secondary={`${order.senders_address} · ZIP: ${order.senders_zip ?? "—"} · ${order.senders_phone}`}
                />
              </ListItem>
            );
          })}
        </List>

        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
          <Button variant="outlined" disabled={optimizing} onClick={handleOptimize}>
            {optimizing ? <CircularProgress size={18} /> : "Optimizar orden"}
          </Button>
          <Button variant="contained" color="secondary" onClick={() => setApproveDialogOpen(true)}>
            Aprobar y asignar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejecting}
            onClick={() => setConfirmReject(true)}
          >
            {rejecting ? <CircularProgress size={18} color="inherit" /> : "Rechazar ruta"}
          </Button>
        </Box>
      </AccordionDetails>

      <ConfirmDialog
        open={!!confirmRemoveBoxId}
        title="Quitar orden"
        message="¿Confirmas que deseas quitar esta orden de la ruta?"
        onClose={(confirmed) => {
          if (confirmed) handleRemoveOrder(confirmRemoveBoxId);
          else setConfirmRemoveBoxId(null);
        }}
      />

      <ConfirmDialog
        open={confirmReject}
        title="Rechazar ruta"
        message="¿Confirmas que deseas rechazar y eliminar esta ruta completa? Esta acción no se puede deshacer."
        onClose={(confirmed) => {
          if (confirmed) handleReject();
          else setConfirmReject(false);
        }}
      />

      <ApproveRouteDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApprove}
        creationDate={route.creation_date}
      />

      <MoveOrderDialog
        open={!!moveBoxId}
        onClose={() => setMoveBoxId(null)}
        currentRouteId={route.id}
        currentRouteCreationDate={route.creation_date}
        onConfirmMove={handleMoveOrder}
      />
    </Accordion>
  );
};

export default RouteCard;
